import { useCallback, useRef, useState } from 'react'

import {
	IDeleteHighlightParams,
	IHighlightsFolder,
	IMoveSegmentParams,
	ISegment
} from '@/types/highlight.interface'
import { toastInfo } from '@/utils/toastInfo'

import {
	hasPartialOverlapInFolder,
	hasPartialOverlapInFolders,
	hasSameSegment
} from '../helpers/segmentOverlap'

interface IUseHighlightSegmentActionsParams {
	folders: IHighlightsFolder[]
	pendingSegment: ISegment | null
	closeHighlightMenu: () => void
	addSegmentToFolder: (params: {
		highlightId: number
		segment: ISegment
	}) => Promise<unknown>
	deleteHighlight: (params: IDeleteHighlightParams) => Promise<unknown>
}

export const useHighlightSegmentActions = ({
	folders,
	pendingSegment,
	closeHighlightMenu,
	addSegmentToFolder,
	deleteHighlight
}: IUseHighlightSegmentActionsParams) => {
	const [isOverlapWarningOpen, setIsOverlapWarningOpen] = useState(false)

	const overlapConfirmActionRef = useRef<(() => Promise<void>) | null>(null)

	const closeOverlapWarning = useCallback(() => {
		setIsOverlapWarningOpen(false)
		overlapConfirmActionRef.current = null
	}, [])

	const confirmOverlapWarning = useCallback(async () => {
		const action = overlapConfirmActionRef.current

		setIsOverlapWarningOpen(false)
		overlapConfirmActionRef.current = null

		if (!action) return

		try {
			await action()
		} catch (error) {
			console.log(error)
		}
	}, [])

	const saveSelectedSegmentToFolder = async (folderId: number) => {
		if (!pendingSegment) return

		const targetFolder = folders.find(folder => folder.id === folderId)

		const sameSegment = hasSameSegment(pendingSegment, targetFolder)

		if (sameSegment) {
			toastInfo(
				'This highlight has already been created in the selected folder'
			)
		}

		await addSegmentToFolder({
			highlightId: folderId,
			segment: pendingSegment
		})

		closeHighlightMenu()
	}

	const hasPartialOverlapInSelectedFolders = useCallback(
		(folderIds: number[], segment: ISegment) => {
			return hasPartialOverlapInFolders(folderIds, segment, folders)
		},
		[folders]
	)

	const saveSegment = async (folderIds: number[], segment: ISegment) => {
		const hasSameSegmentInSelectedFolders = folderIds.some(folderId =>
			hasSameSegment(
				segment,
				folders.find(folder => folder.id === folderId)
			)
		)

		if (hasSameSegmentInSelectedFolders) {
			toastInfo(
				'This highlight has already been created in the selected folder'
			)
		}

		await Promise.all(
			folderIds.map(folderId =>
				addSegmentToFolder({
					highlightId: folderId,
					segment
				})
			)
		)
	}

	const saveSelectedSegmentToFolders = async (folderIds: number[]) => {
		if (!pendingSegment || !folderIds.length) return

		const segment = pendingSegment

		closeHighlightMenu()

		if (hasPartialOverlapInSelectedFolders(folderIds, segment)) {
			overlapConfirmActionRef.current = () => saveSegment(folderIds, segment)

			setIsOverlapWarningOpen(true)
			return
		}

		try {
			await saveSegment(folderIds, segment)
		} catch (error) {
			console.log(error)
		}
	}

	const moveSegment = async ({
		sourceFolderId,
		targetFolderId,
		segmentIndex,
		segment
	}: IMoveSegmentParams) => {
		const targetFolder = folders.find(folder => folder.id === targetFolderId)

		const sameSegment = hasSameSegment(segment, targetFolder)

		if (sameSegment) {
			toastInfo(
				'This highlight has already been created in the selected folder'
			)
		}

		try {
			await addSegmentToFolder({
				highlightId: targetFolderId,
				segment
			})

			await deleteHighlight({
				highlightId: sourceFolderId,
				index: segmentIndex
			})
		} catch (error) {
			console.log(error)
		}
	}

	const moveSegmentToFolder = async ({
		sourceFolderId,
		targetFolderId,
		segmentIndex,
		segment
	}: IMoveSegmentParams) => {
		if (sourceFolderId === targetFolderId) return

		const targetFolder = folders.find(folder => folder.id === targetFolderId)

		const hasOverlapInTargetFolder = hasPartialOverlapInFolder(
			targetFolder,
			segment
		)

		if (hasOverlapInTargetFolder) {
			overlapConfirmActionRef.current = () =>
				moveSegment({ sourceFolderId, targetFolderId, segmentIndex, segment })

			setIsOverlapWarningOpen(true)
			return
		}

		await moveSegment({ sourceFolderId, targetFolderId, segmentIndex, segment })
	}

	return {
		saveSelectedSegmentToFolder,
		saveSelectedSegmentToFolders,
		moveSegmentToFolder,
		isOverlapWarningOpen,
		closeOverlapWarning,
		confirmOverlapWarning
	}
}
